import React, {useMemo, useReducer} from 'react';
import {FormGroup} from '@blueprintjs/core';

import {BaseBlock, ContentActions, ContentOptions} from '../types';
import {ContentCard, ContentToolbar} from '../components';
import {defaultContentActions, defaultContentOptions} from '../constants';
import {initialParameterContentState} from './store';
import InputFactory from './InputFactory';
import {useCreateBlock, useInitBlock, useUpdateBlockContentJson} from '../hooks';
import {createDefaultContent} from './utils';
import {
  contentReducer,
  setContentFormKeyValue,
} from '../store';
import {TextList, TextListOptions} from './components';
import {DEFAULT_TEXT_LIST, INPUT_TYPE, OPTIONS} from './constants';

import './Parameter.scss';

interface ParameterProps {
  content: BaseBlock;
  options?: ContentOptions;
  actions?: ContentActions;
}

export const Parameter = ({
  content,
  options = defaultContentOptions,
  actions = defaultContentActions,
}: ParameterProps) => {
  const [contentState, contentDispatch] = useReducer(contentReducer, initialParameterContentState);

  options = useMemo((): ContentOptions => ({...defaultContentOptions, ...options}), [options?.readOnly]);
  actions = useMemo((): ContentActions => ({...defaultContentActions, ...actions}), []);

  const isTextList = contentState.form.parameter_type === INPUT_TYPE.TEXT_LIST;
  const uid = content.uid;

  const {updating, updateBlockContentJson} = useUpdateBlockContentJson(uid, options.readOnly, options.autosave);
  useCreateBlock(content, createDefaultContent());
  useInitBlock(uid, content, contentDispatch);

  const onValueChange = (value: string) => {
    updateBlockContentJson('parameter_value', value);
    contentDispatch(setContentFormKeyValue('parameter_value', value));
  };

  const onValueOptionsChange = (values: any[]) => {
    updateBlockContentJson('parameter_value_options', values);
    contentDispatch(setContentFormKeyValue('parameter_value_options', values));
  }

  const onTypeChange = (value: any) => {
    const type = value;

    updateBlockContentJson('parameter_type', type);
    contentDispatch(setContentFormKeyValue('parameter_type', type));
  };

  return (
    <ContentCard
      key={content.id}
      type={content.type}
      contentId={contentState.init ? uid : null}
      disabled={options.readOnly}
      noContentElement={undefined}
      allowCollapse={options.allowCollapse}
      showHeader={options.showHeader}
      showFooter={options.showFooter}
    >
      <div className="content__parameter">
        <ContentToolbar
          contentId={uid as string}
          selected={contentState.form.parameter_type}
          options={OPTIONS}
          onSelect={onTypeChange}
          updating={updating}
          onDelete={actions.onDelete}
          showDelete={options.showDelete}
          showSave={false}
          disabled={options.readOnly}
        />
        <div className="content-card__content__form">
          <InputFactory
            type={contentState.form.parameter_type}
            value={contentState.form.parameter_value}
            disabled={options.readOnly}
            onChange={onValueChange}
          />
          {
            /*
             * Handled separately from Input Factory components (those are simple inputs)
             */
            isTextList && (
              <FormGroup label="" className="text-list-select">
                <TextList
                  value={contentState.form.parameter_value}
                  options={contentState.form.parameter_value_options}
                  disabled={options.readOnly || false}
                  onChange={onValueChange}
                />
                <TextListOptions
                  options={contentState.form.parameter_value_options || DEFAULT_TEXT_LIST}
                  onChange={onValueOptionsChange}
                />
              </FormGroup>
            )
          }
        </div>
      </div>
    </ContentCard>
  );
};
